import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {CompletionDatesDto, WorksModel} from "src/app/models/WorksModel";
import {
  isBook,
  isComic,
  isGame,
  isManga,
  isSeries,
} from "src/app/utils/works-type-guards.util";
import {GameCompletion, VolumeProgress} from "src/app/modules/auth/models/User";
import {ToastService} from "../../services/toast.service";
import {Episode, Season, Series} from "src/app/models/Series";
import {DateAdapter} from "@angular/material/core";
import {MyDateAdapter} from "src/app/helpers/MyDateAdapter";
import {GameCompletionLabels} from "src/app/enums/Game";
import {Book} from "src/app/models/Book";
import {Game} from "src/app/models/Game";
import {Manga} from "src/app/models/Manga";
import {Comic} from "src/app/models/Comic";
import {MatFormField, MatHint, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from "@angular/material/datepicker";
import {TranslateModule} from "@ngx-translate/core";
import {clampDateToToday, getDateSafely} from "src/app/helpers/date";

@Component({
  selector: "app-edit-work-completion-dates",
  templateUrl: "./edit-work-completion-dates.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: DateAdapter, useClass: MyDateAdapter}],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    TranslateModule,
  ],
})
export class EditWorkCompletionDatesComponent implements OnInit {
  @Input() item!: WorksModel;
  @Input() season?: Season;
  @Input() episode?: Episode;
  @Input() episodeNumber?: number;
  @Input() completion?: GameCompletion;
  @Input() volume?: VolumeProgress;
  @Output() dateChange = new EventEmitter<CompletionDatesDto>();
  dateForm: FormGroup;
  public isSeries = isSeries;
  public isGame = isGame;
  public isBook = isBook;
  public isComic = isComic;
  public isManga = isManga;

  constructor(
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService
  ) {
    this.dateForm = this.fb.group({
      selectedDate: ["", Validators.required],
    });
  }

  ngOnInit() {
    this.setCompletionDate(this.item);
  }

  get dateHint(): string {
    if (!isSeries(this.item)) return "";
    if (!this.dateForm.get("selectedDate")?.value) return "WATCH_DATE_UNKNOWN";
    return this.item.progress?.episodeUpdates?.some(
      (update) =>
        update.seasonNumber === this.season?.seasonNumber &&
        update.episodeNumber === this.episodeNumber &&
        update.estimatedDate
    )
      ? "WATCH_DATE_ESTIMATED"
      : "";
  }

  setCompletionDate(item: WorksModel): void {
    const date = this.computeCompletionDate(item);
    if (date) {
      this.dateForm.setValue({selectedDate: clampDateToToday(date)});
    }
  }

  private computeCompletionDate(item: WorksModel): Date | null {
    if (isGame(item) || isBook(item)) {
      return this.getGameOrBookDate(item);
    }
    if (isManga(item) || isComic(item)) {
      return this.getVolumeDate(item);
    }
    if (isSeries(item) && this.season && this.episodeNumber != null) {
      return this.getSeriesDate(item);
    }
    return null;
  }

  private getGameOrBookDate(item: WorksModel): Date | null {
    const updates = (item as Game | Book).progress?.completionUpdates ?? [];
    const currentLabel = this.completion?.completion;
    const existing = updates.find((u) => u.completion === currentLabel);
    if (existing || isBook(item)) {
      return existing?.date ?? null;
    }
    const sorted = updates
      .slice()
      .sort(
        (a, b) =>
          this.completionWeight(b.completion) -
          this.completionWeight(a.completion)
      );
    const next = currentLabel
      ? sorted.find(
          (u) =>
            this.completionWeight(u.completion) >
            this.completionWeight(currentLabel)
        )
      : undefined;
    return next?.date ?? null;
  }

  private getVolumeDate(item: WorksModel): Date | null {
    const volumes = (item as Manga | Comic).progress?.volumes ?? [];
    const match = volumes.find((v) => v.volume === this.volume?.volume);
    return match?.date ?? null;
  }

  private getSeriesDate(item: WorksModel): Date | null {
    const episodes = (item as Series).progress?.episodeUpdates ?? [];
    const exact = episodes.find(
      (e) =>
        e.seasonNumber === this.season?.seasonNumber &&
        e.episodeNumber === this.episodeNumber
    );
    if (exact) {
      return exact.date;
    }
    if (
      this.season?.seasonNumber === 1 &&
      this.episodeNumber === 1 &&
      !episodes.some((update) => update.date === null)
    ) {
      return getDateSafely(item.discoveryDate);
    }
    const sorted = episodes
      .slice()
      .sort((a, b) =>
        a.seasonNumber === b.seasonNumber
          ? a.episodeNumber - b.episodeNumber
          : a.seasonNumber - b.seasonNumber
      );
    const next = sorted.find(
      (e) =>
        (this.season !== undefined &&
          this.episodeNumber &&
          e.seasonNumber === this.season?.seasonNumber &&
          e.episodeNumber > this.episodeNumber) ||
        (this.season !== undefined && e.seasonNumber > this.season.seasonNumber)
    );
    return next?.date ?? null;
  }

  private completionWeight(label: string): number {
    switch (label) {
      case GameCompletionLabels.mainStory:
        return 1;
      case GameCompletionLabels.mainExtras:
        return 2;
      case GameCompletionLabels.platinum:
        return 3;
      default:
        return 0;
    }
  }
  onInputChange(): void {
    const selectedDate = this.dateForm.get("selectedDate")?.value;

    if (!selectedDate) return;

    const clampedSelectedDate = clampDateToToday(selectedDate);
    if (clampedSelectedDate.getTime() !== selectedDate.getTime()) {
      this.dateForm.setValue({selectedDate: clampedSelectedDate});
    }

    const clampedDateString = clampedSelectedDate.toISOString();
    let completionDateDto: CompletionDatesDto;

    if (isSeries(this.item) && this.season && this.episodeNumber) {
      completionDateDto = {
        season: this.season?.seasonNumber,
        episode: this.episodeNumber,
        date: clampedDateString,
      };
    } else if ((isGame(this.item) || isBook(this.item)) && this.completion) {
      completionDateDto = {
        completion: this.completion?.completion,
        date: clampedDateString,
      };
    } else if ((isManga(this.item) || isComic(this.item)) && this.volume) {
      completionDateDto = {
        volume: this.volume.volume,
        date: clampedDateString,
      };
    } else {
      this.toastService.sendToast({
        text: "Error during update. Please retry later",
        classes: "bg-form-error",
      });
      return;
    }

    this.dateChange.emit(completionDateDto);
  }
}
